import { useEffect } from 'react'
import { Box, Stack, styled, Typography, useTheme, useMediaQuery } from '@mui/material'
import { getCurrentUser } from '@/db/indexedDB'

import {
  EMAIL_VERIFICATION_KEY,
  LENDING_PLATFORM,
  PHONE_VERIFICATION_KEY,
  VERIFICATION_STATUS,
} from '@/utils/constants'
import { Chip } from '@/components/UI/Chip/index'
import { Copy } from '@/components/UI/Copy'
import {
  MailOutlinedIcon,
  NotVerifiedIcon,
  PhoneOutlinedIcon,
  VerifiedIcon,
} from '@/components/UI/Icons'
import { useAppStore } from '@/store'
import { RiskScore } from '@/components/RiskScore'
import { CashflowScore } from '@/components/CashflowScore'
import { Kyc } from '@/components/Kyc'

export const Profile: React.FC = () => {
  const { user, setUser } = useAppStore()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const transformStatus = (status: string | undefined) => status?.toLowerCase() ?? ''

  useEffect(() => {
    const load = async () => {
      const storedUser = await getCurrentUser()
      if (storedUser) setUser(storedUser)
    }
    load()
  }, [])

  const renderInfo = (IconComponent: React.ElementType, value?: string, status?: string) => {
    const lowercaseVal = status?.toLowerCase()
    const NEGATIVE_STATUS =
      typeof status === 'string' &&
      (lowercaseVal === VERIFICATION_STATUS.FAILED ||
        lowercaseVal === VERIFICATION_STATUS.NOT_VERIFIED ||
        lowercaseVal === VERIFICATION_STATUS.NOT_VERIFIED_ENROLLED_MFA)

    const renderVerificationStatus = () => {
      if (!status || status === 'undefined') return null
      return (
        <Chip
          value={transformStatus(status)}
          icon={NEGATIVE_STATUS ? NotVerifiedIcon : VerifiedIcon}
        />
      )
    }
    if (!value) return null

    return (
      <Box>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{
            minWidth: 'fit-content',
          }}
        >
          <IconComponent sx={{ fontSize: '16px', flexShrink: 0 }} />
          <Copy value={value ?? 'N/A'} />
          {renderVerificationStatus()}
        </Stack>
      </Box>
    )
  }

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          maxWidth: '100%',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
        }}
      >
        <StyledBox mb={isMobile ? 2 : 1}>
          <Stack direction="row" alignItems="center" gap={1} mb={1} flexWrap="wrap">
            <Typography
              component={'h6'}
              sx={{
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: 'bold',
                wordBreak: 'break-word',
              }}
            >
              {fullName || 'Unnamed User'}
            </Typography>
            {user?.isOnLendingPlatform && <Chip value={LENDING_PLATFORM} chipColor="#EAA221" />}
          </Stack>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Stack direction="column" gap={2}>
              {renderInfo(MailOutlinedIcon, user?.email, user?.[EMAIL_VERIFICATION_KEY])}
              {renderInfo(PhoneOutlinedIcon, user?.mobile, user?.[PHONE_VERIFICATION_KEY])}
            </Stack>
          </Box>
        </StyledBox>
      </Box>

      <Box mt={2}>
        <RiskScore />
      </Box>

      <Box mt={4}>
        <Kyc />
      </Box>

      <Box mt={4}>
        <CashflowScore />
      </Box>
    </>
  )
}

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flex: 1,
}))

export const Label = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 'bold',
  fontFamily: 'Inter',
  color: '#344054',
}))

export const Value = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 'normal',
  fontFamily: 'Inter',
  color: theme.palette.secondary.main,
}))
